import { useEffect } from 'react'
import { useParams } from '@tanstack/react-router'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'

import { useTenantWorkspace } from '@/features/workspace/use-tenant-workspace'
import { useRedo, useUndo } from './queries'
import { undoScopes } from './scopes'
import { isEditableTarget, undoRedoIntent } from './shortcut'

/**
 * Binds undo and redo to the keyboard for as long as the shell is mounted.
 *
 * Mount it once. The listener is on `window` rather than on a container, because
 * the keystroke has to work with focus anywhere -- including on nothing, which
 * is where focus sits after most clicks.
 */
export function useUndoRedoShortcut(): void {
  const { t } = useTranslation()
  const undo = useUndo()
  const redo = useRedo()

  const { workspace } = useTenantWorkspace()
  const params = useParams({ strict: false })

  const scopes = undoScopes({
    workspaceId: workspace?.id,
    applicationId: params.databaseId ?? params.dashboardId,
    tableId: params.tableId,
  })

  const pending = undo.isPending || redo.isPending
  const undoMutate = undo.mutate
  const redoMutate = redo.mutate

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      const intent = undoRedoIntent({
        key: event.key,
        ctrlKey: event.ctrlKey,
        metaKey: event.metaKey,
        shiftKey: event.shiftKey,
        inEditableField: isEditableTarget(event.target),
      })
      if (intent === null) return

      // Claimed only once it is ours. Returning before this leaves the browser's
      // own undo intact for anything this does not handle.
      event.preventDefault()

      // One at a time. The keystroke repeats while held, and the stack is
      // walked server-side: two in flight would undo two actions for one press
      // and report them in whichever order they happened to return.
      if (pending) return

      const report = (result: { result_code: string }) => {
        if (result.result_code === 'NOTHING_TO_DO') {
          toast(
            t(intent === 'undo' ? 'undoRedo.nothingToUndo' : 'undoRedo.nothingToRedo'),
          )
        } else if (result.result_code === 'SKIPPED_DUE_TO_ERROR') {
          toast.error(t('undoRedo.skipped'))
        }
      }

      if (intent === 'undo') {
        undoMutate(scopes, { onSuccess: report })
      } else {
        redoMutate(scopes, { onSuccess: report })
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [scopes, pending, undoMutate, redoMutate, t])
}
